import type { FC, ReactNode } from 'react'
import type { DeliveryTier } from '@/types'

import { Mail, MapPin, Phone } from 'lucide-react'
import { useSiteContent } from '@/lib/site-content'
import CmsText from '@/components/generals/cms-text'
import { FadeIn } from '@/components/motion/fade-in'

interface InfoCardProps {
    deliveryTiers: DeliveryTier[]
}

const InfoCard: FC<InfoCardProps> = ({ deliveryTiers }): ReactNode => {
    const { settings } = useSiteContent()

    return (
        <FadeIn delay={0.05}>
            <aside className='lg:pl-6'>
                <CmsText as='h3' textKey='order.info.title' className='font-display text-3xl font-extrabold text-neutral-900' />

                <hr className='mt-4 w-12 border-0 border-t-2 border-brand-yellow' />

                <p className='mt-6 font-display text-xl font-extrabold'>
                    <CmsText textKey='order.info.brand.first' className='text-brand-red' />{' '}
                    <CmsText textKey='order.info.brand.second' className='text-neutral-900' />
                </p>

                <div className='mt-5 space-y-2 text-sm text-neutral-700'>
                    <p className='flex items-start gap-2'>
                        <MapPin aria-hidden className='mt-0.5 h-4 w-4 text-brand-red' />

                        <span>
                            <span className='block' data-mdlfy-cms={`site_settings:${settings.id}:address_line_1`}>
                                {settings.address_line_1}
                            </span>

                            <span className='block' data-mdlfy-cms={`site_settings:${settings.id}:address_line_2`}>
                                {settings.address_line_2}
                            </span>
                        </span>
                    </p>

                    <p className='flex items-center gap-2'>
                        <Mail aria-hidden className='h-4 w-4 text-brand-red' />

                        <a
                            href={`mailto:${settings.contact_email}`}
                            className='hover:text-brand-red'
                            data-mdlfy-cms={`site_settings:${settings.id}:contact_email`}
                        >
                            {settings.contact_email}
                        </a>
                    </p>
                </div>

                <div className='mt-6 rounded-card bg-white/85 p-5 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]'>
                    <h4 className='font-display text-base font-bold text-neutral-900'>
                        <CmsText textKey='delivery.title' />:
                    </h4>

                    <ul className='mt-3 space-y-2 text-sm text-neutral-700'>
                        {deliveryTiers.map(tier => (
                            <li key={tier.id} className='flex items-center justify-between gap-3'>
                                <span>
                                    <span className='text-brand-red'>○</span>{' '}
                                    <strong className='text-neutral-900' data-mdlfy-cms={`delivery_tiers:${tier.id}:quantity_range`}>
                                        {tier.quantity_range}
                                    </strong>{' '}
                                    <span className='text-neutral-600' data-mdlfy-cms={`delivery_tiers:${tier.id}:label`}>
                                        {tier.label}
                                    </span>
                                </span>

                                <span className='font-bold text-neutral-900' data-mdlfy-cms={`delivery_tiers:${tier.id}:price`}>
                                    {tier.price}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <a
                    href={`mailto:${settings.contact_email}`}
                    className='mt-6 inline-flex items-center gap-2 rounded-pill bg-brand-yellow px-5 py-2.5 font-display text-sm font-semibold text-brand-red-dark shadow-soft-yellow transition-transform hover:scale-[1.02]'
                >
                    <Phone aria-hidden className='h-4 w-4' />
                    <CmsText textKey='order.info.contactCta' />
                </a>
            </aside>
        </FadeIn>
    )
}

export default InfoCard
